import React, { useState, useEffect, useCallback, useRef } from "react";

const SUPPORTED_TRACKERS = [
  { id: "rutracker", label: "RuTracker", color: "#ff6b35" },
  { id: "nnmclub", label: "NNM-Club", color: "#4ecdc4" },
  { id: "piratebay", label: "PirateBay", color: "#45b7d1" },
  { id: "1337x", label: "1337x", color: "#96ceb4" },
  { id: "rarbg", label: "RARBG", color: "#feca57" },
];

const TRACKER_ID_ALIASES = {
  l337x: "1337x",
};

const GlobalSearchResults = ({
  searchQuery,
  selectedTrackers,
  onDownloadTorrent,
  telegramId,
}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [expandedResult, setExpandedResult] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingIndex, setDownloadingIndex] = useState(null);

  // Используем ref для отслеживания последнего запроса
  const lastSearchQueryRef = useRef("");
  const lastTrackersRef = useRef("");
  const abortControllerRef = useRef(null);

  const totalResultsRef = useRef(null);
  const pageCacheRef = useRef(new Map());

  const getPageCacheKey = (query, trackers, page) =>
    `${query.trim().toLowerCase()}|${[...trackers].sort().join(",")}|${page}`;

  const searchGlobal = useCallback(
    async (query, page = 1, trackers) => {
      if (!query.trim()) {
        setResults([]);
        setCurrentPage(1);
        setTotalPages(0);
        return;
      }

      if (query.trim().length > 100) {
        setError("Search query is too long. Maximum 100 characters.");
        setResults([]);
        setCurrentPage(1);
        setTotalPages(0);
        return;
      }

      const cacheKey = getPageCacheKey(query, trackers, page);
      const cachedPage = pageCacheRef.current.get(cacheKey);
      if (cachedPage) {
        setResults(cachedPage.results);
        setTotalPages(cachedPage.totalPages);
        setCurrentPage(page);
        totalResultsRef.current = cachedPage.total;
        setError(null);
        setLoading(false);
        console.log(`[GlobalSearch] Page cache hit: ${cacheKey}`);
        return;
      }

      // Отменяем предыдущий запрос если он еще выполняется
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Создаем новый AbortController для этого запроса
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      console.log(
        `[GlobalSearch] Starting search: query="${query}", page=${page}, trackers=${trackers.join(
          ",",
        )}`,
      );

      try {
        const trackerParams = trackers.join(",");
        const response = await fetch("/api/global-search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
          },
          body: JSON.stringify({
            query: query.trim(),
            trackers: trackerParams,
            page,
            limit: 25,
            telegramId,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
            if (response.status === 401 || errorData.status_code === 401) {
              errorMessage += " (Check API token or token limit exceeded)";
            }
          } catch {
            if (response.status === 401) {
              errorMessage += " (Check API token or token limit exceeded)";
            }
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        console.log(`[GlobalSearch] Request completed successfully`);

        if (data.success) {
          setResults(data.results || []);

          const effectiveTotal =
            data.total !== null && data.total !== undefined
              ? data.total
              : totalResultsRef.current || 0;

          if (data.total !== null && data.total !== undefined) {
            totalResultsRef.current = data.total;
          }

          const pageSize = data.limit || 25;
          const totalPagesValue =
            pageSize > 0 ? Math.ceil(effectiveTotal / pageSize) || 0 : 0;
          setTotalPages(totalPagesValue);
          setCurrentPage(page);

          pageCacheRef.current.set(cacheKey, {
            results: data.results || [],
            total: effectiveTotal,
            totalPages: totalPagesValue,
          });

          console.log(
            `[GlobalSearch] Pagination: page=${page}, total=${effectiveTotal}, pages=${Math.ceil(
              effectiveTotal / pageSize,
            )}`,
          );
        } else {
          let errorMessage = data.message || "Search failed";
          if (data.status_code === 401) {
            errorMessage += " (Check API token or token limit exceeded)";
          }
          throw new Error(errorMessage);
        }
      } catch (error) {
        // Игнорируем ошибки отмены запроса
        if (error.name === "AbortError") {
          console.log(`[GlobalSearch] Request was aborted`);
          return;
        }
        console.error("Global search error:", error);
        setError(error.message);
        setResults([]);
        setCurrentPage(1);
        setTotalPages(0);
      } finally {
        setLoading(false);
        abortControllerRef.current = null;
      }
    },
    [telegramId],
  );

  // Сбрасываем состояние и начинаем новый поиск при изменении запроса или трекеров
  useEffect(() => {
    const trackersString = selectedTrackers.join(",");

    console.log(
      `[GlobalSearch] useEffect triggered - query: "${searchQuery}", trackers: "${trackersString}"`,
    );
    console.log(
      `[GlobalSearch] Last query: "${lastSearchQueryRef.current}", Last trackers: "${lastTrackersRef.current}"`,
    );

    // Проверяем, действительно ли изменился запрос или трекеры
    if (
      searchQuery !== lastSearchQueryRef.current ||
      trackersString !== lastTrackersRef.current
    ) {
      console.log(
        `[GlobalSearch] Change detected, resetting state and starting new search`,
      );

      // Обновляем refs
      lastSearchQueryRef.current = searchQuery;
      lastTrackersRef.current = trackersString;

      totalResultsRef.current = null;
      pageCacheRef.current = new Map();

      // Сбрасываем состояние
      setResults([]);
      setCurrentPage(1);
      setTotalPages(0);
      setExpandedResult(null);
      setError(null);

      if (searchQuery && searchQuery.trim()) {
        searchGlobal(searchQuery, 1, selectedTrackers);
      }
    } else {
      console.log(`[GlobalSearch] No change detected, skipping search`);
    }

    // Cleanup function для отмены запросов при размонтировании
    return () => {
      if (abortControllerRef.current) {
        console.log(`[GlobalSearch] Cleanup: aborting ongoing request`);
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedTrackers]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && !loading) {
      searchGlobal(searchQuery, page, selectedTrackers);
    }
  };

  const formatSize = (value) => {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }

    if (typeof value !== "number" || Number.isNaN(value)) {
      return "N/A";
    }

    if (value === 0) return "0 B";

    const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
    const index = Math.floor(Math.log(value) / Math.log(1024));
    const safeIndex = Math.min(index, sizes.length - 1);
    const normalized = value / Math.pow(1024, safeIndex);
    return `${Math.round(normalized * 100) / 100} ${sizes[safeIndex]}`;
  };

  const getTrackerInfo = (trackerName) => {
    const raw = String(trackerName || "Unknown");
    const aliased = TRACKER_ID_ALIASES[raw.toLowerCase()] || raw;
    const tracker = SUPPORTED_TRACKERS.find(
      (t) =>
        aliased.toLowerCase() === t.id.toLowerCase() ||
        aliased.toLowerCase().includes(t.id.toLowerCase()) ||
        t.label.toLowerCase().includes(aliased.toLowerCase()),
    );
    return tracker || { id: "unknown", label: raw, color: "#666" };
  };

  const handleDownload = async (result, index) => {
    try {
      setDownloadingIndex(index);
      await onDownloadTorrent(result);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setDownloadingIndex(null);
    }
  };

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-center">
        <div className="text-red-400 font-semibold mb-2">Search Error</div>
        <div className="text-red-300 text-sm">{error}</div>
      </div>
    );
  }

  if (!searchQuery) {
    return (
      <div className="text-center py-6 text-gray-400">
        <div className="flex justify-center mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            className="text-[#03e9f4]"
          >
            <title>Globe-search-24-filled SVG Icon</title>
            <path
              fill="currentColor"
              d="M3.441 9.956a4.926 4.926 0 0 0 6.233 7.571l4.256 4.257a.773.773 0 0 0 1.169-1.007l-.075-.087l-4.217-4.218A4.927 4.927 0 0 0 3.44 9.956m13.213 6.545c-.225 1.287-.548 2.456-.952 3.454l.03.028l.124.14c.22.295.344.624.378.952a10.03 10.03 0 0 0 4.726-4.574zM12.25 16.5l2.284 2.287c.202-.6.381-1.268.53-1.992l.057-.294zm-2.936-5.45a3.38 3.38 0 1 1-4.78 4.779a3.38 3.38 0 0 1 4.78-4.78M15.45 10h-3.7a5.94 5.94 0 0 1 .892 5h2.71a26 26 0 0 0 .132-4.512zm1.507 0a28 28 0 0 1-.033 4.42l-.057.58h4.703a10.05 10.05 0 0 0 .258-5zm-2.095-7.593c.881 1.35 1.536 3.329 1.883 5.654l.062.44h4.59a10.03 10.03 0 0 0-6.109-5.958l-.304-.1zm-2.836-.405c-1.277 0-2.561 2.382-3.158 5.839c.465.16.912.38 1.331.658l5.088.001c-.54-3.809-1.905-6.498-3.261-6.498m-2.837.405A10.03 10.03 0 0 0 2.654 8.5h.995a5.92 5.92 0 0 1 3.743-.968c.322-1.858.846-3.47 1.527-4.68l.162-.275z"
            />
          </svg>
        </div>
        <div className="text-lg font-semibold mb-2">Global Search Mode</div>
        <div className="text-sm">
          Enter a search query to find torrents across multiple trackers
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#03e9f4]"></div>
        <div className="ml-4 text-[#03e9f4] font-semibold">
          Searching globally...
        </div>
      </div>
    );
  }

  if (results.length === 0 && !loading) {
    return (
      <div className="text-center py-12 text-gray-400">
        <div className="text-lg font-semibold mb-2">No Results Found</div>
        <div className="text-sm">
          Try different keywords or enable more trackers
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Results */}
      <div className="space-y-2">
        {results.map((result, index) => {
          const trackerInfo = getTrackerInfo(result.tracker || "Unknown");
          const isExpanded = expandedResult === index;
          const formattedSize = formatSize(
            result.size_text || result.size_bytes || result.size,
          );

          return (
            <div
              key={`${searchQuery}-${currentPage}-${index}-${result.tracker}-${
                result.id || result.title
              }`}
              className={`global-search-card ${
                isExpanded ? "global-search-card-expanded" : ""
              }`}
            >
              {/* Top section with title */}
              <div className="global-search-card-top">
                <p
                  className="global-search-card-title"
                  onClick={() => setExpandedResult(isExpanded ? null : index)}
                >
                  {result.title || result.name || "Untitled"}
                </p>
              </div>

              {/* Bottom section with info and download button */}
              <div className="global-search-card-desc">
                {/* Tracker Badge */}
                <div
                  className="global-search-badge"
                  style={{
                    backgroundColor: `${trackerInfo.color}26`,
                    color: trackerInfo.color,
                    borderColor: `${trackerInfo.color}4D`,
                  }}
                >
                  {trackerInfo.label}
                </div>

                {/* Downloads */}
                {(result.downloads || result.snatches) && (
                  <div className="global-search-info-item gs-meta-downloads">
                    <span className="global-search-info-label">Downloads:</span>
                    <span className="global-search-info-value">
                      {result.downloads || result.snatches || 0}
                    </span>
                  </div>
                )}

                {/* Seeders */}
                {(result.seeders !== undefined ||
                  result.leechers !== undefined) && (
                  <div className="global-search-info-item gs-meta-seeders">
                    <span className="global-search-info-label">Seeders:</span>
                    <span className="global-search-stat-value seeders">
                      {result.seeders || 0}
                    </span>
                  </div>
                )}

                {/* Leechers */}
                {(result.seeders !== undefined ||
                  result.leechers !== undefined) && (
                  <div className="global-search-info-item gs-meta-leechers">
                    <span className="global-search-info-label">Leechers:</span>
                    <span className="global-search-stat-value leechers">
                      {result.leechers || 0}
                    </span>
                  </div>
                )}

                {/* Size */}
                <div className="global-search-info-item gs-meta-size">
                  <span className="global-search-info-label">Size:</span>
                  <span className="global-search-info-value">
                    {formattedSize}
                  </span>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(result, index)}
                  className={`global-search-download-btn ${
                    downloadingIndex === index ? "loading" : ""
                  }`}
                  disabled={downloadingIndex === index}
                  title={
                    downloadingIndex === index
                      ? "Downloading..."
                      : "Download Torrent"
                  }
                >
                  <div className="loader"></div>
                  <svg
                    className="svgIcon"
                    viewBox="0 0 384 512"
                    height="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"></path>
                  </svg>
                  <span className="icon2"></span>
                </button>
              </div>

              {isExpanded && (result.category || result.description) && (
                <div className="global-search-card-expanded-content">
                  {result.category || result.description}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          {/* Previous Button */}
          <div
            onClick={() =>
              currentPage > 1 && !loading && handlePageChange(currentPage - 1)
            }
            className={`pagination-number arrow ${
              currentPage <= 1 || loading ? "disabled" : ""
            }`}
          >
            <svg
              className="pagination-arrow-icon"
              viewBox="0 0 512 512"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="48"
                d="M328 112L184 256l144 144"
              />
            </svg>
            <span className="arrow-text">Previous</span>
          </div>

          {/* Page Numbers */}
          {(() => {
            const pages = [];
            const showPages = 5; // Количество видимых страниц

            if (totalPages <= showPages + 2) {
              // Показываем все страницы если их мало
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              // Всегда показываем первую страницу
              pages.push(1);

              if (currentPage <= 3) {
                // Начало списка
                pages.push(2, 3, 4);
              } else if (currentPage >= totalPages - 2) {
                // Конец списка
                pages.push(totalPages - 3, totalPages - 2, totalPages - 1);
              } else {
                // Середина списка
                pages.push(currentPage - 1, currentPage, currentPage + 1);
              }

              // Всегда показываем последнюю страницу
              if (!pages.includes(totalPages)) {
                pages.push(totalPages);
              }
            }

            return pages.map((pageNum, index) => (
              <div
                key={pageNum}
                onClick={() => !loading && handlePageChange(pageNum)}
                className={`pagination-number ${
                  currentPage === pageNum ? "pagination-active" : ""
                } ${loading ? "disabled" : ""}`}
              >
                {pageNum}
              </div>
            ));
          })()}

          {/* Next Button */}
          <div
            onClick={() =>
              currentPage < totalPages &&
              !loading &&
              handlePageChange(currentPage + 1)
            }
            className={`pagination-number arrow ${
              currentPage >= totalPages || loading ? "disabled" : ""
            }`}
          >
            <svg
              className="pagination-arrow-icon"
              viewBox="0 0 512 512"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="48"
                d="M184 112l144 144l-144 144"
              />
            </svg>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#03e9f4]"></div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchResults;
